import type {
    Conversation,
    ConversationMessage,
    ConversationMeta,
    ConversationParticipant,
    ConversationType,
    MinimalApplication,
    MinimalCompany,
    MinimalCompanyBranch,
    MinimalStudent,
    NullableId,
    User,
} from '../client';

export class ConversationModel implements Conversation {
    id: number;
    media_storage_id: NullableId;
    title: string;
    conversation_type: ConversationType;
    thumbnail_url?: string | null | undefined;
    meta?: ConversationMeta | null;
    closed_at?: Conversation['closed_at'];
    deleted_at?: Conversation['deleted_at'];
    created_at: Conversation['created_at'];
    updated_at: Conversation['updated_at'];
    participants?: ConversationParticipant[] | undefined;
    recent_messages?: { has_more: boolean; messages: ConversationMessage[] } | undefined;
    my_participant?: ConversationParticipant | null | undefined;
    latest_message_id?: NullableId | undefined;
    company_branches?: MinimalCompanyBranch[] | undefined;
    companies?: MinimalCompany[] | undefined;
    student?: (null | MinimalStudent) | undefined;
    application?: (null | MinimalApplication) | undefined;

    constructor(data: Conversation) {
        this.id = data.id;
        this.media_storage_id = data.media_storage_id;
        this.title = data.title;
        this.conversation_type = data.conversation_type;
        this.thumbnail_url = data.thumbnail_url;
        this.meta = data.meta;
        this.closed_at = data.closed_at;
        this.deleted_at = data.deleted_at;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
        this.participants = data.participants;
        this.recent_messages = data.recent_messages;
        this.my_participant = data.my_participant;
        this.latest_message_id = data.latest_message_id;
        this.company_branches = data.company_branches;
        this.companies = data.companies;
        this.student = data.student;
        this.application = data.application;
    }

    getDynamicTitle(viewer?: User | null): string {
        if (viewer == null) {
            return this.title;
        }

        if (this.conversation_type === 'application' || this.conversation_type === 'service') {
            return this.title;
        }

        if (this.conversation_type === 'support') {
            if (viewer.account_type === 'staff') {
                return (
                    this.company_branches?.find(branch => branch.id !== viewer.company_branch_id)?.display_name ??
                    this.companies?.find(company => company.id !== viewer.company_id)?.display_name ??
                    this.title
                );
            }
            return this.title;
        }

        if (this.conversation_type === 'inter_company') {
            if (
                viewer.account_type === 'agency' ||
                (viewer.account_type === 'school' && this.participants?.some(p => p.company_type === 'student'))
            ) {
                const participant = this.participants?.find(p => p.company_type === 'student');
                if (participant?.name) {
                    return participant.name;
                }
            }
        }

        const otherBranches = this.company_branches?.filter(branch => branch.id !== viewer.company_branch_id) ?? [];
        return otherBranches.length === 0
            ? this.title
            : otherBranches
                  .map(
                      branch =>
                          (branch.company_type === 'student' ? branch.company?.display_name : branch.display_name) ??
                          branch.display_name,
                  )
                  .join(', ');
    }
}
